'use client'

import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cn } from '../../utils'

export interface PortaledPopoverContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  /**
   * Hard cap for the popover's height in pixels. The popover is sized
   * to `min(maxHeight, --radix-popover-content-available-height)`, so
   * it never extends past the viewport edge in short windows.
   * @default 320
   */
  maxHeight?: number
}

/**
 * A pre-configured Radix Popover content for dropdowns / autocompletes
 * that need to escape a parent Dialog's clipping while remaining
 * scrollable.
 *
 * What it bundles, so callers don't have to repeat any of it:
 *
 * 1. **Portal escape.** Renders through `Popover.Portal`, so the
 *    popover lives in `<body>` rather than inside the dialog's
 *    `overflow:hidden` + transform stacking context.
 *
 * 2. **Hard height cap that respects the viewport.** Inline
 *    `maxHeight: min(<cap>, var(--radix-popover-content-available-height))`
 *    keeps the popover within the design cap *and* the viewport — the
 *    inline `style` always wins over Tailwind so the cap can't be
 *    accidentally lost in a build.
 *
 * 3. **Flex column layout.** The popover is `flex flex-col`, so a
 *    direct child marked `flex-1 min-h-0` claims the full popover
 *    height. Combined with `useScrollableInPortal` on that child,
 *    the inner list scrolls within the cap regardless of how many
 *    items it renders.
 *
 * 4. **Dialog whitelist.** Radix Popover automatically sets
 *    `[data-radix-popper-content-wrapper]` on this element, which the
 *    shared `Dialog` already whitelists in its `onPointerDownOutside`
 *    — so clicking inside the dropdown does not dismiss the dialog.
 *
 * Pair with `useScrollableInPortal()` from `@rr/ui` on the inner
 * scrollable child to restore wheel/touch scrolling that
 * `react-remove-scroll` (used by Radix Dialog) would otherwise kill
 * for portaled content.
 *
 * @example
 *   const portalScrollRef = useScrollableInPortal<HTMLDivElement>()
 *   <Popover open={open} onOpenChange={setOpen}>
 *     <PopoverPrimitive.Anchor asChild>...</PopoverPrimitive.Anchor>
 *     <PortaledPopoverContent maxHeight={320}>
 *       <div ref={portalScrollRef} className="flex-1 min-h-0 overflow-y-auto">
 *         {items}
 *       </div>
 *     </PortaledPopoverContent>
 *   </Popover>
 */
export const PortaledPopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PortaledPopoverContentProps
>(
  (
    {
      maxHeight = 320,
      className,
      style,
      align = 'start',
      side = 'bottom',
      sideOffset = 4,
      collisionPadding = 8,
      ...props
    },
    ref,
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ ref }
        align={ align }
        side={ side }
        sideOffset={ sideOffset }
        collisionPadding={ collisionPadding }
        style={ {
          maxHeight: `min(${maxHeight}px, var(--radix-popover-content-available-height))`,
          ...style,
        } }
        className={ cn(
          'z-[9999] w-[var(--radix-popper-anchor-width)] rounded-md border border-border bg-popover p-0 text-popover-foreground shadow-md outline-none overflow-hidden',
          'flex flex-col',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'origin-[--radix-popover-content-transform-origin]',
          className,
        ) }
        { ...props }
      />
    </PopoverPrimitive.Portal>
  ),
)
PortaledPopoverContent.displayName = 'PortaledPopoverContent'
